import { ERRORS } from '@/common/constants/errors';
import {
  transformCard,
  transformCardTokens,
  transformCards,
  transformStatus,
} from '@/common/integrations/menhir/employee/card/card.dto';
import { menhirClient } from '@/common/integrations/menhir/menhir.client';
import {
  MENHIR_ENDPOINTS,
  endpointFor,
} from '@/common/integrations/menhir/menhir.endpoints';
import { IAddress } from '@/common/types/general';
import {
  CardStatus,
  ICard,
  IMenhirCard,
  IMenhirCardEnvelope,
  IMenhirCardStatus,
  IMenhirCardTokens,
  IMenhirCardsEnvelope,
  TCardDetails,
  TNormalizedCardDetails,
} from './card.types';
import {
  buildExternalDetails,
  decryptDetails,
  generateCardDetailsSessionKey,
  isEncryptedCardDetails,
  isExternalCardDetails,
} from './card.utils';

export const getEmployeeCards = (): Promise<ICard[]> =>
  menhirClient
    .get<null, IMenhirCardsEnvelope>(MENHIR_ENDPOINTS.EMPLOYEE.CARD.CARDS)
    .then(transformCards);

export const lockCard = (cardId: IMenhirCard['id']): Promise<CardStatus> =>
  menhirClient
    .patch<
      null,
      IMenhirCardStatus
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.CARD.LOCK, cardId))
    .then(transformStatus);

export const unlockCard = (cardId: IMenhirCard['id']): Promise<CardStatus> =>
  menhirClient
    .patch<
      null,
      IMenhirCardStatus
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.CARD.UNLOCK, cardId))
    .then(transformStatus);

export const activateCard = ({
  cardId,
  panConfirmation,
}: {
  cardId: IMenhirCard['id'];
  panConfirmation: string;
}): Promise<CardStatus> =>
  menhirClient
    .patch<null, IMenhirCardStatus>(
      endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.CARD.ACTIVATE, cardId),
      {
        pan_confirmation: panConfirmation,
      }
    )
    .then(transformStatus);

export const cancelCard = (cardId: IMenhirCard['id']): Promise<CardStatus> =>
  menhirClient
    .delete<
      null,
      IMenhirCardStatus
    >(`${MENHIR_ENDPOINTS.EMPLOYEE.CARD.ROOT}/${cardId}`)
    .then(transformStatus);

export const requestCard = ({
  shippingAddress,
  cardHolderName,
}: {
  shippingAddress?: IAddress;
  cardHolderName?: string;
}): Promise<ICard> =>
  menhirClient
    .post<null, IMenhirCardEnvelope>(MENHIR_ENDPOINTS.EMPLOYEE.CARD.ROOT, {
      card: {
        shipping_address: shippingAddress
          ? {
              address_line_1: shippingAddress.addressLine1,
              address_line_2: shippingAddress.addressLine2,
              zipcode: shippingAddress.zipCode,
              type: 'card_shipping_address',
              country: shippingAddress.country,
              city: shippingAddress.city,
              district: shippingAddress.district,
            }
          : undefined,
        holder_name: cardHolderName,
      },
    })
    .then(transformCard);

export const requestPhysicalCard = ({
  cardId,
  shippingAddress,
  cardHolderName,
}: {
  cardId: IMenhirCard['id'];
  shippingAddress?: IAddress;
  cardHolderName?: string;
}): Promise<ICard> =>
  menhirClient
    .post<null, IMenhirCardEnvelope>(
      endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.CARD.DETAIL, cardId),
      {
        card: {
          shipping_address: shippingAddress
            ? {
                address_line_1: shippingAddress.addressLine1,
                address_line_2: shippingAddress.addressLine2,
                zipcode: shippingAddress.zipCode,
                type: 'card_shipping_address',
                country: shippingAddress.country,
                city: shippingAddress.city,
                district: shippingAddress.district,
              }
            : undefined,
          holder_name: cardHolderName,
        },
      }
    )
    .then(transformCard);

export const requestPin = (cardId: IMenhirCard['id']): Promise<void> =>
  menhirClient.post(
    endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.CARD.REQUEST_PIN_SMS, cardId)
  );

export const requestPinToken = () =>
  menhirClient
    .get<
      null,
      IMenhirCardTokens
    >(MENHIR_ENDPOINTS.EMPLOYEE.CARD.PIN.ONE_TIME_TOKEN)
    .then(transformCardTokens);

export const resetPin = ({
  encryptedPin,
  oneTimeToken,
}: {
  encryptedPin: string;
  oneTimeToken: string;
}) =>
  menhirClient.post<
    null,
    null,
    {
      encrypted_pin: string;
      one_time_token: string;
    }
  >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.CARD.PIN.SET), {
    encrypted_pin: encryptedPin,
    one_time_token: oneTimeToken,
  });

export const validateCardName = (cardHolderName: string) =>
  menhirClient.post(
    MENHIR_ENDPOINTS.EMPLOYEE.CARD.ROOT,
    {
      card: { holder_name: cardHolderName },
    },
    {
      params: {
        validate: true,
      },
    }
  );

async function getDetailsPublicKey(cardId: string) {
  return menhirClient
    .get<
      null,
      {
        public_key: string;
      }
    >(endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.CARD.DETAILS.PUBLIC_KEY, cardId))
    .then((response) => response.public_key);
}

async function getDetails(cardId: string, sessionId?: string) {
  return menhirClient.get<{ session_id: string }, TCardDetails>(
    endpointFor(MENHIR_ENDPOINTS.EMPLOYEE.CARD.DETAILS.ROOT, cardId),
    {
      params: {
        session_id: sessionId,
      },
    }
  );
}

export async function requestCardDetails(
  cardId: string,
  cardStatus: ICard['status'],
  requiresEncryption?: boolean
) {
  return requiresEncryption
    ? requestEncryptedCardDetails(cardId)
    : requestExternalCardDetails(cardId, cardStatus);
}

async function requestEncryptedCardDetails(
  cardId: string
): Promise<TNormalizedCardDetails | undefined> {
  const publicKey = await getDetailsPublicKey(cardId);
  const { encKey, sessionKey } = await generateCardDetailsSessionKey(publicKey);
  const details = await CardService.getDetails(cardId, sessionKey);
  if (isEncryptedCardDetails(details)) {
    const decryptedDetails = await decryptDetails(encKey, details);
    return decryptedDetails;
  }
}

async function requestExternalCardDetails(
  cardId: string,
  cardStatus: ICard['status']
): Promise<TNormalizedCardDetails | undefined> {
  const details = await CardService.getDetails(cardId);
  if (!details.cvv) {
    throw new Error(ERRORS.CARD_DETAILS_FETCHING_ERROR);
  }
  if (isExternalCardDetails(details)) {
    return buildExternalDetails(details, cardStatus);
  }
}

export const CardService = {
  getEmployeeCards,
  lockCard,
  unlockCard,
  activateCard,
  cancelCard,
  requestCard,
  requestPhysicalCard,
  requestPin,
  requestPinToken,
  resetPin,
  validateCardName,
  getDetailsPublicKey,
  getDetails,
  requestCardDetails,
};
